'use client';

import { Composite } from '@floating-ui/react';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import { useControllableValue } from '../../hooks/use-controllable-value';
import { TabsRootContext } from './context/tabs-root.context';
import type { TabsRootPropsInterface } from './types/tabs-root.types';

/**
 * A `Tabs.Root` component acts as the container for `Tabs.List` and `Tabs.Panels`.
 *
 * If you aren't using `Tabs.Panel` components, you can use `Tabs.List` without this `Tabs.Root` parent,
 * if you prefer.
 *
 * @subcomponents ['Tabs.List', 'Tabs.Panel', 'Tabs.Tab', 'Tabs.TabIcon']
 * @since 8.10.0
 */

export const TabsRoot = forwardRef<HTMLDivElement, TabsRootPropsInterface>(
  (props: TabsRootPropsInterface, forwardedRef) => {
    const { children, ...restProps } = props;

    const {
      appearance = 'default',
      className,
      css: cssProp,
      defaultSelected,
      fluid = false,
      disabled = false,
      manualActivation,
      onSelectedChange,
      selected,
      ...rest
    } = restProps;

    const [selectedState, setSelectedState] = useControllableValue<string | undefined>({
      controlledValue: selected,
      uncontrolledValue: defaultSelected,
      isControlled: 'selected' in props,
      isUncontrolled: 'defaultSelected' in props,
      onChange: (newSelected) => {
        if (newSelected !== undefined) {
          onSelectedChange?.(newSelected);
        }
      },
      componentName: 'Tabs.Root',
      controlledPropName: 'selected',
      uncontrolledPropName: 'defaultSelected',
    });

    const allTabs = useChildrenOfType(children, 'Tabs.Tab');

    const initialTabIndex = useMemo(() => {
      return selectedState ? allTabs.findIndex((tab) => tab.props.value === selectedState) : 0;
    }, [allTabs, selectedState]);

    const [activeIndex, setActiveIndex] = useState(initialTabIndex);

    /**
     * `Tabs.Tab` sets `tabIndex` from selection state and discards Composite's
     * roving tabindex, so this effect doesn't control which tab is tabbable.
     * It keeps Floating UI's `activeIndex` (the arrow-key cursor) in sync when
     * selection changes from a click or a controlled `selected` prop, so
     * keyboard nav resumes from the selected tab.
     */
    useEffect(() => {
      const index = allTabs.findIndex((tab) => tab.props.value === selectedState);
      if (index !== -1) {
        setActiveIndex(index);
      }
    }, [allTabs, selectedState]);

    const onTabsFocusChange = useCallback(
      (index: number) => {
        setActiveIndex(index);

        if (!manualActivation) {
          // If the tab at the new index is disabled, don't change panel content
          const tabAtNewIndex = allTabs?.[index];
          if (tabAtNewIndex?.props.disabled) {
            return;
          }
          setSelectedState(allTabs?.[index]?.props.value);
        }
      },
      [allTabs, manualActivation, setSelectedState],
    );

    const contextValue = useMemo(
      () => ({
        appearance,
        fluid,
        disabled,
        manualActivation,
        selected: selectedState,
        setSelected: setSelectedState,
      }),
      [appearance, fluid, disabled, manualActivation, selectedState, setSelectedState],
    );

    return (
      <TabsRootContext.Provider value={contextValue}>
        {/* Intentionally not allowing user overrides for Composite */}
        <Composite
          loop
          activeIndex={activeIndex}
          onNavigate={onTabsFocusChange}
          disabledIndices={[]}
          orientation="horizontal"
        >
          <div
            ref={forwardedRef}
            data-c11n-component="Tabs.Root"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', css(cssProp), className)}
            {...rest}
          >
            {children}
          </div>
        </Composite>
      </TabsRootContext.Provider>
    );
  },
);

TabsRoot.displayName = 'Tabs.Root';
